import React, { useState, useEffect, useCallback } from 'react';
import { authService } from '../services/authService';
import { candidateService } from '../services/candidateService';
import Layout from '../components/Layout';
import PasswordChangeModal from '../components/PasswordChangeModal';
import CandidateDrawer from '../components/CandidateDrawer';
import FitScoreExplainer from '../components/FitScoreExplainer';
import ScopeFilterBar from '../components/ScopeFilterBar';
import ScoringBanner from '../components/ScoringBanner';
import { useScoringState } from '../hooks/useScoringState';

// Candidate Graph, on fit-v2.0.
//
// The page leads with a BUCKET and two or three evidence sentences, and carries
// the fit score alongside them. The ordering of those three is the design: the
// bucket says whether to act, the sentences say why — "worked at Beta Foods, a
// client" is something a recruiter can check — and the number breaks ties inside
// a band, which is where most of the work actually happens.
//
// The score was deliberately withheld at first, to stop the page becoming what
// V0 was: a number with nothing behind it. It is shown now because the evidence
// is there to back it, not instead of it.
//
// A candidate with no evidence at all is absent rather than ranked last. That
// absence is a product statement: we know nothing about them you could act on.

const BUCKETS = [
  {
    key: 'call_now',
    label: 'Call now',
    blurb: 'Strong evidence and a way in.',
    chip: 'bg-emerald-100 text-emerald-800 ring-emerald-600/20',
    score: 'bg-emerald-100 text-emerald-800',
    dot: 'bg-emerald-500',
    rule: 'border-l-emerald-400',
    num: 'text-emerald-700',
  },
  {
    key: 'warm',
    label: 'Warm',
    blurb: 'A real relationship worth reviving.',
    chip: 'bg-amber-100 text-amber-800 ring-amber-600/20',
    score: 'bg-amber-100 text-amber-800',
    dot: 'bg-amber-500',
    rule: 'border-l-amber-400',
    num: 'text-amber-700',
  },
  {
    key: 'track',
    label: 'Track',
    blurb: 'Some history; keep an eye on them.',
    chip: 'bg-sky-100 text-sky-800 ring-sky-600/20',
    score: 'bg-sky-100 text-sky-800',
    dot: 'bg-sky-500',
    rule: 'border-l-sky-400',
    num: 'text-sky-700',
  },
  {
    key: 'low',
    label: 'Background',
    blurb: 'Thin evidence — visible, not actionable.',
    chip: 'bg-gray-100 text-gray-700 ring-gray-500/20',
    score: 'bg-gray-100 text-gray-700',
    dot: 'bg-gray-400',
    rule: 'border-l-gray-300',
    num: 'text-gray-600',
  },
];

const bucketMeta = (key) => BUCKETS.find((b) => b.key === key) || BUCKETS[3];

// Contact-channel icons.
//
// These were the dingbats ✉ and ☎ (U+2709, U+260E). Those are TEXT characters,
// not icons: they take their shape from whichever font the OS resolves them to,
// so the envelope arrived hairline on one machine and solid on another, and the
// telephone rendered as a 1970s rotary handset. Neither matched the stroked
// 24×24 set used everywhere else in the app.
//
// Same paths as Layout.jsx and the classic view's Actions column, so a contact
// icon means the same thing and looks the same wherever it appears.
const MailIcon = () => (
  <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
    />
  </svg>
);

const PhoneIcon = () => (
  <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
    />
  </svg>
);

const PAGE_SIZE = 50;

// Density is a per-user preference, not a per-visit one: someone who scans in
// list view wants list view again tomorrow. Kept in localStorage rather than in
// the URL because it says nothing about WHICH candidates are being looked at —
// a shared link should carry the filter, not the reader's habits.
const VIEW_KEY = 'signalcue.candidateGraph.view';
const VIEW_CARDS = 'cards';
const VIEW_LIST = 'list';

function loadView() {
  try {
    const v = window.localStorage.getItem(VIEW_KEY);
    return v === VIEW_LIST || v === VIEW_CARDS ? v : VIEW_CARDS;
  } catch (e) {
    // Private mode / storage disabled. A density preference is not worth an
    // error boundary, so fall back to the default and move on.
    return VIEW_CARDS;
  }
}

function ChannelIcons({ channels }) {
  const hasEmail = !!channels?.email;
  const hasPhone = !!channels?.phone;
  if (!hasEmail && !hasPhone) {
    return <span className="text-gray-400">No contact details on file</span>;
  }
  return (
    <>
      {hasEmail && (
        <span className="inline-flex items-center gap-1.5">
          <MailIcon />
          email
        </span>
      )}
      {hasPhone && (
        <span className="inline-flex items-center gap-1.5">
          <PhoneIcon />
          phone
        </span>
      )}
    </>
  );
}

function BucketChip({ bucket }) {
  const meta = bucketMeta(bucket);
  return (
    <span
      data-testid="candidate-bucket"
      className={`text-xs font-medium px-2.5 py-1 rounded-full ring-1 ring-inset whitespace-nowrap ${meta.chip}`}
    >
      {meta.label}
    </span>
  );
}

// The number sits BESIDE the bucket, not instead of it. The bucket answers
// "should I act"; the score answers "which of these two first" — the question
// the bucket alone cannot, since most of a book lands in one band.
function ScoreBadge({ score, bucket, size = 'w-9 h-9 text-sm' }) {
  const meta = bucketMeta(bucket);
  return (
    <span
      data-testid="candidate-score"
      title="Fit score"
      className={`${size} shrink-0 rounded-lg flex items-center justify-center font-bold ${meta.score}`}
    >
      {score}
    </span>
  );
}

// The LIST view.
//
// The first cut was cards with the padding removed, and it read as a wall: no
// column to track down, the bucket chip and "email phone" repeated on every one
// of ~45 rows, and the evidence joined into a "·"-separated run-on that
// truncated mid-sentence. Repetition that never varies carries no information;
// it is just weight.
//
// So this is built as a table would be — fixed columns, one scan axis, and each
// fact stated at the level it actually changes:
//
//   · the SCORE keeps the filled square badge the card uses. One number should
//     not look like two different things depending on the view.
//   · the BAND is a chip on each row, ahead of the reach icons: the coarser
//     judgement (act at all?) reads before the finer one (how to reach them?).
//     It was briefly a section header instead — fewer repetitions, but it made
//     the band a property of a region rather than of the person, and it broke
//     down the moment a filter left only one group.
//   · the EVIDENCE is every reason, one per line, each truncated on its own so
//     a sentence never breaks mid-clause.
//   · CHANNELS hold a fixed slot whether present or not, so the column does not
//     ripple as it descends.

function CandidateRow({ candidate: c, onOpen }) {
  const reasons = c.reasons || [];

  return (
    <button
      data-testid="candidate-row"
      onClick={onOpen}
      className={`group w-full text-left grid grid-cols-[3.25rem_minmax(0,1fr)] sm:grid-cols-[3.25rem_minmax(0,18rem)_minmax(0,1fr)_auto]
                  items-start gap-x-4 px-4 py-3.5 border-b border-gray-100 last:border-b-0
                  hover:bg-slate-50 focus:outline-none focus-visible:bg-slate-50 transition-colors`}
    >
      {/* The square badge, as on the card. It is the one element that has to
          survive between the two views unchanged: it is how a score is
          recognised at a glance, and a different treatment per view would make
          the same number look like two different things. */}
      <ScoreBadge score={c.fit_score} bucket={c.bucket} size="w-11 h-11 text-base" />

      <div className="min-w-0">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="text-sm font-semibold text-gray-900 truncate">{c.full_name}</span>
          {c.local_name && (
            <span className="text-xs text-gray-400 truncate shrink-0 max-w-[7rem]">{c.local_name}</span>
          )}
        </div>
        <div className="text-xs text-gray-500 truncate mt-0.5">
          {[c.current_title, c.current_company].filter(Boolean).join(' · ') || 'Role unknown'}
        </div>
        {/* Below sm the evidence column is gone, so the channels come here
            rather than disappearing with it. */}
        <div className="sm:hidden mt-2 flex items-center gap-3 text-xs text-gray-500">
          <ChannelIcons channels={c.channels} />
        </div>
      </div>

      {/* Evidence — ALL of it. This is the reason the row is allowed to be
          tall: the sentences are what make the score believable, and a list
          that hides two of the three to save 20px is optimising the wrong
          thing. Each is truncated on its own line so the bullets stay a
          readable column rather than a reflowing paragraph. */}
      <ul className="hidden sm:block min-w-0 space-y-1" data-testid="candidate-reasons">
        {reasons.map((r, i) => (
          <li key={r.code || i} className="flex gap-2 text-sm text-gray-700 leading-5">
            <span className="text-gray-300 select-none shrink-0">•</span>
            <span className="truncate">{r.text}</span>
          </li>
        ))}
        {reasons.length === 0 && <li className="text-sm text-gray-400 leading-5">No evidence recorded</li>}
      </ul>

      {/* Band, then reach. The chip sits ahead of the icons because it is the
          coarser judgement — whether to act at all comes before how to make
          contact. */}
      <div className="hidden sm:flex items-center justify-end gap-3 pt-1.5">
        <BucketChip bucket={c.bucket} />
        <div className="flex items-center gap-2 text-gray-400">
          <span
            className={c.channels?.email ? 'text-gray-500' : 'text-gray-200'}
            title={c.channels?.email ? 'Email on file' : 'No email'}
          >
            <MailIcon />
          </span>
          <span
            className={c.channels?.phone ? 'text-gray-500' : 'text-gray-200'}
            title={c.channels?.phone ? 'Phone on file' : 'No phone'}
          >
            <PhoneIcon />
          </span>
        </div>
      </div>
    </button>
  );
}

// One candidate as a card.
function CandidateCard({ candidate: c, onOpen }) {
  return (
    <button
      data-testid="candidate-card"
      onClick={onOpen}
      className="text-left bg-white rounded-xl shadow-sm p-5 border border-gray-100 hover:border-gray-300 hover:shadow transition"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold text-gray-900 truncate">
            {c.full_name}
            {c.local_name && <span className="ml-2 text-gray-400 font-normal">{c.local_name}</span>}
          </div>
          <div className="text-sm text-gray-500 truncate mt-0.5">
            {[c.current_title, c.current_company].filter(Boolean).join(' · ') || 'Role unknown'}
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-2">
          <BucketChip bucket={c.bucket} />
          <ScoreBadge score={c.fit_score} bucket={c.bucket} />
        </div>
      </div>

      <ul className="mt-3 space-y-1.5" data-testid="candidate-reasons">
        {(c.reasons || []).map((r, i) => (
          <li key={r.code || i} className="flex gap-2 text-sm text-gray-700">
            <span className="text-gray-300 select-none">•</span>
            <span>{r.text}</span>
          </li>
        ))}
      </ul>

      <div className="mt-4 flex items-center gap-3 text-xs text-gray-500">
        <ChannelIcons channels={c.channels} />
        {[c.city, c.country].filter(Boolean).length > 0 && (
          <span className="ml-auto">{[c.city, c.country].filter(Boolean).join(', ')}</span>
        )}
      </div>
    </button>
  );
}

export default function CandidateGraph() {
  const [user, setUser] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [counts, setCounts] = useState({});
  const [model, setModel] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // A tenant that has never been scored gets 'computing' or 'failed' here
  // instead of a silently empty `candidates` list. Defaults to 'ready' so the
  // banner stays out of the way before the first response lands -- `loading`
  // already covers that window.
  const [scoringState, setScoringState] = useState('ready');
  const [scoringMessage, setScoringMessage] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [bucket, setBucket] = useState('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [view, setView] = useState(loadView);

  const [scope, setScope] = useState(null);
  // Deliberately component state and nothing else -- no localStorage, no URL
  // parameter the browser restores. Every visit opens on the desk, so one click
  // last month cannot leave a recruiter in the firehose for weeks.
  const [dims, setDims] = useState('both');

  const loadUserData = useCallback(async () => {
    try {
      const userData = await authService.getCurrentUser();
      if (!userData) {
        window.location.href = `${window.location.origin}/login`;
        return;
      }
      setUser(userData);
      if (userData.must_change_password) setShowPasswordModal(true);
    } catch (e) {
      window.location.href = `${window.location.origin}/login`;
    }
  }, []);

  useEffect(() => {
    loadUserData();
  }, [loadUserData]);

  useEffect(() => {
    let live = true;
    authService
      .getMyScope()
      .then((s) => live && setScope(s))
      // A desk that fails to load must not take the page down with it: the list
      // still works, it is just unscoped, which is the safe direction to fail.
      .catch(() => live && setScope(null));
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_KEY, view);
    } catch (e) {
      /* storage disabled — the toggle still works for this session */
    }
  }, [view]);

  // Debounce the search box: one request per pause in typing, not per keystroke.
  useEffect(() => {
    const id = setTimeout(() => {
      setQuery(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(id);
  }, [search]);

  const loadCandidates = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await candidateService.getCandidates({
        bucket,
        search: query,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        dims,
      });
      setCandidates(data.candidates || []);
      setCounts(data.buckets || {});
      setModel(data.model || null);
      setTotal(data.total || 0);
      setScoringState(data.scoring_state || 'ready');
      setScoringMessage(data.scoring_message || '');
    } catch (e) {
      setError(e.message || 'Failed to load candidates');
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, [bucket, query, page, dims]);

  useEffect(() => {
    if (user && !user.must_change_password) loadCandidates();
  }, [user, loadCandidates]);

  const { elapsedSeconds } = useScoringState(scoringState, loadCandidates);

  const openCandidate = async (card) => {
    setSelected(card);
    setDrawerOpen(true);
    try {
      const detail = await candidateService.getCandidateProfile(card.person_id);
      setSelected(detail);
    } catch (e) {
      /* the card already on screen stays; the drawer just shows less */
    }
  };

  const scoredTotal = BUCKETS.reduce((sum, b) => sum + (counts[b.key] || 0), 0);
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <Layout user={user}>
      {showPasswordModal && (
        <PasswordChangeModal
          isOpen={showPasswordModal}
          onClose={() => setShowPasswordModal(false)}
          onSuccess={() => {
            setShowPasswordModal(false);
            loadUserData();
          }}
        />
      )}

      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Candidate Graph</h1>
          <p className="text-sm text-gray-500 mt-1">
            Who you already know, how well, and whether you can reach them.
            {scoredTotal > 0 && (
              <>
                {' '}
                <span className="text-gray-700 font-medium" data-testid="scored-total">
                  {scoredTotal.toLocaleString()}
                </span>{' '}
                candidates have evidence behind them.
              </>
            )}
          </p>
        </div>
        <a
          href="/candidates/classic"
          data-testid="switch-to-classic"
          className="shrink-0 text-sm text-[#1B3A6B] hover:underline whitespace-nowrap mt-1"
        >
          Switch to classic view →
        </a>
      </div>

      {/* Bucket rail — the primary navigation, because the bucket IS the answer.
          It reads as four stat cards unless it says otherwise, so the
          interactivity is stated three ways: a heading that names the rail as a
          control, a filter glyph on every tile, and real motion on hover. The
          first of those is the one that works BEFORE the pointer arrives, which
          is when a user decides whether something is clickable at all. */}
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <h2 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          Filter by band
        </h2>
        {bucket && (
          <button
            type="button"
            data-testid="bucket-clear"
            onClick={() => {
              setBucket('');
              setPage(0);
            }}
            className="text-xs font-medium text-[#1B3A6B] hover:underline"
          >
            Clear filter
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {BUCKETS.map((b) => {
          const active = bucket === b.key;
          return (
            <button
              key={b.key}
              type="button"
              data-testid={`bucket-${b.key}`}
              aria-pressed={active}
              title={active ? `Showing only ${b.label} — click to clear` : `Show only ${b.label}`}
              onClick={() => {
                setBucket(active ? '' : b.key);
                setPage(0);
              }}
              className={`group relative text-left rounded-xl p-4 border cursor-pointer
                transition-all duration-150 focus:outline-none
                focus-visible:ring-2 focus-visible:ring-[#1B3A6B] focus-visible:ring-offset-2 ${
                  active
                    ? 'border-[#1B3A6B] ring-2 ring-[#1B3A6B]/20 bg-[#1B3A6B]/[0.04] shadow-sm'
                    : 'border-gray-200 bg-white hover:border-gray-400 hover:shadow-md hover:-translate-y-0.5 active:translate-y-0'
                }`}
            >
              <div className="flex items-center gap-2">
                <span className={`inline-block w-2 h-2 rounded-full ${b.dot}`} />
                <span className="text-sm font-medium text-gray-900">{b.label}</span>
                {/* Always present, never loud: visible enough to read as a
                    control at rest, and it resolves to a clear ✕ once the
                    filter is on, because the same click now removes it. */}
                <span
                  aria-hidden="true"
                  className={`ml-auto shrink-0 transition-colors ${
                    active ? 'text-[#1B3A6B]' : 'text-gray-300 group-hover:text-gray-500'
                  }`}
                >
                  {active ? (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L15 12.414V19a1 1 0 01-.553.894l-4 2A1 1 0 019 21v-8.586L3.293 6.707A1 1 0 013 6V4z" />
                    </svg>
                  )}
                </span>
              </div>

              <div className="mt-2 text-2xl font-semibold text-gray-900">
                {(counts[b.key] || 0).toLocaleString()}
              </div>
              <div className="mt-1 text-xs text-gray-500 leading-snug">{b.blurb}</div>

              {/* The state line: permanent when the filter is on (it explains
                  what the list is showing), and a hover hint otherwise. */}
              <div
                className={`mt-2 text-[11px] font-medium transition-opacity ${
                  active
                    ? 'text-[#1B3A6B] opacity-100'
                    : 'text-gray-400 opacity-0 group-hover:opacity-100'
                }`}
              >
                {active ? 'Showing only these — click to clear' : 'Click to filter'}
              </div>
            </button>
          );
        })}
      </div>

      <FitScoreExplainer model={model} />

      <div className="bg-white rounded-xl shadow-sm p-4 mb-6 flex items-center gap-3">
        <input
          type="text"
          data-testid="candidate-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name…"
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1B3A6B] focus:border-[#1B3A6B] text-sm"
        />
        {/* Density toggle. A segmented pair rather than an icon-only button:
            two words cost a few pixels and remove all doubt about which state
            is current, which an icon that changes meaning by context cannot. */}
        <div
          role="group"
          aria-label="Result layout"
          className="shrink-0 inline-flex rounded-lg border border-gray-300 overflow-hidden"
        >
          {[
            { key: VIEW_CARDS, label: 'Cards' },
            { key: VIEW_LIST, label: 'List' },
          ].map((v) => {
            const on = view === v.key;
            return (
              <button
                key={v.key}
                type="button"
                data-testid={`view-${v.key}`}
                aria-pressed={on}
                onClick={() => setView(v.key)}
                className={`px-3 py-2 text-sm font-medium transition-colors ${
                  on ? 'bg-[#1B3A6B] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {v.label}
              </button>
            );
          })}
        </div>

        {(bucket || search) && (
          <button
            type="button"
            data-testid="clear-all"
            onClick={() => {
              setBucket('');
              setSearch('');
              setPage(0);
            }}
            className="text-sm text-gray-500 hover:text-gray-800 shrink-0"
          >
            Clear all
          </button>
        )}
      </div>

      <ScopeFilterBar scope={scope} dims={dims} onChange={setDims} />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 mb-6 text-sm" data-testid="candidate-error">
          {error}
        </div>
      )}

      <ScoringBanner
        state={scoringState}
        message={scoringMessage}
        elapsedSeconds={elapsedSeconds}
        onRetry={loadCandidates}
      />

      {loading ? (
        <div className="text-sm text-gray-500 py-12 text-center" data-testid="candidate-loading">
          Loading candidates…
        </div>
      ) : candidates.length === 0 ? (
        // A page still computing or failed must never look the same as a
        // genuinely empty result -- the banner above already explains that
        // case, so the ordinary empty state is suppressed until scoring
        // has actually settled on 'ready'.
        scoringState === 'ready' ? (
          <div className="bg-white rounded-xl shadow-sm p-10 text-center" data-testid="candidate-empty">
            <p className="text-gray-900 font-medium">No candidates match this view.</p>
            <p className="text-sm text-gray-500 mt-2">
              Candidates appear here once there is evidence behind them — a client
              they worked at, someone you both know, or contact history. Nobody is
              listed on a guess.
            </p>
          </div>
        ) : null
      ) : (
        <>
          {view === VIEW_LIST ? (
            <div
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
              data-testid="candidate-list"
            >
              {candidates.map((c) => (
                <CandidateRow key={c.person_id} candidate={c} onOpen={() => openCandidate(c)} />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="candidate-list">
              {candidates.map((c) => (
                <CandidateCard key={c.person_id} candidate={c} onOpen={() => openCandidate(c)} />
              ))}
            </div>
          )}

          <div className="flex items-center justify-between mt-6 text-sm text-gray-600">
            <span data-testid="candidate-total">
              {total.toLocaleString()} in this view
            </span>
            <div className="flex items-center gap-2">
              <button
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-40"
              >
                Previous
              </button>
              <span>
                Page {page + 1} of {lastPage + 1}
              </span>
              <button
                disabled={page >= lastPage}
                onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                className="px-3 py-1.5 rounded-lg border border-gray-300 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {drawerOpen && selected && (
        <CandidateDrawer
          candidate={selected}
          isOpen={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setSelected(null);
          }}
        />
      )}
    </Layout>
  );
}
